import type { Media, Segment, Take, Transcript, Word } from './types'

export function tcFromFrames(frames: number, fps: number): string {
  const base = Math.round(fps)
  const f = Math.max(0, Math.floor(frames))
  const pad = (n: number) => String(n).padStart(2, '0')
  const s = Math.floor(f / base)
  return `${pad(Math.floor(s / 3600) % 24)}:${pad(Math.floor(s / 60) % 60)}:${pad(s % 60)}:${pad(f % base)}`
}

export function takeTc(take: Take, t: number): string {
  if (take.tc_start == null) return clock(t)
  return tcFromFrames(take.tc_start + Math.floor(t * take.fps + 1e-6), take.fps)
}

export function clock(t: number): string {
  const s = Math.max(0, Math.floor(t))
  const pad = (n: number) => String(n).padStart(2, '0')
  return s >= 3600 ? `${Math.floor(s / 3600)}:${pad(Math.floor(s / 60) % 60)}:${pad(s % 60)}` : `${Math.floor(s / 60)}:${pad(s % 60)}`
}

export function dur(t: number): string {
  if (t < 60) return `${Math.round(t)}s`
  const m = Math.floor(t / 60)
  const s = Math.round(t % 60)
  return s ? `${m}m ${s}s` : `${m}m`
}

export const speakerName = (take: Take, raw?: string) => (raw ? take.speakers[raw] || raw : '')

export type FlatWord = { w: string; s: number; e: number; seg: number; i: number }
export function flatten(tr: Transcript | undefined): FlatWord[] {
  const out: FlatWord[] = []
  tr?.segments.forEach((seg, si) => seg.words.forEach((w: Word) => out.push({ w: w[0], s: w[1], e: w[2], seg: si, i: out.length })))
  return out
}

/** Index of the word playing at time t (last word starting at or before t). */
export function wordAt(words: FlatWord[], t: number): number {
  let lo = 0
  let hi = words.length - 1
  let ans = -1
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    if (words[mid].s <= t + 0.02) {
      ans = mid
      lo = mid + 1
    } else hi = mid - 1
  }
  return ans
}

export function textBetween(tr: Transcript | undefined, start: number, end: number): string {
  if (!tr) return ''
  const ws: string[] = []
  for (const seg of tr.segments) for (const w of seg.words) if (w[1] >= start - 0.05 && w[2] <= end + 0.05) ws.push(w[0])
  return ws.join(' ')
}

export function speakerAt(tr: Transcript | undefined, start: number, end: number): string | undefined {
  return tr?.segments.find((s) => s.end > start && s.start < end && s.speaker)?.speaker
}

/** Paragraphs: consecutive segments from one speaker with short gaps. */
export type Para = { speaker?: string; start: number; end: number; segs: number[] }
export function paragraphs(segs: Segment[]): Para[] {
  const out: Para[] = []
  segs.forEach((s, i) => {
    const last = out[out.length - 1]
    const words = last ? last.segs.reduce((n, k) => n + segs[k].words.length, 0) : 0
    if (last && last.speaker === s.speaker && s.start - last.end < 2.5 && words < 140) {
      last.segs.push(i)
      last.end = s.end
    } else out.push({ speaker: s.speaker, start: s.start, end: s.end, segs: [i] })
  })
  return out
}

/** Which angle plays take time t: preferred if it covers t, else any that does. */
export function angleCovering(take: Take, media: Map<string, Media>, t: number, preferred?: string | null) {
  const covers = (a: Take['angles'][number]) => {
    const d = media.get(a.media_id)?.duration ?? 0
    return t >= a.offset - 0.05 && t <= a.offset + d + 0.05
  }
  const pref = take.angles.find((a) => a.media_id === preferred)
  if (pref && covers(pref)) return pref
  return take.angles.find(covers) || pref || take.angles[0] || null
}

export const COLORS: Record<string, string> = {
  orange: '#FF2800', yellow: '#F5C400', green: '#2ECC71', blue: '#3D8BFF', purple: '#A66BFF', red: '#FF4D6D',
}

export function friendlyPassword(): string {
  const words = ['amber', 'basil', 'cedar', 'delta', 'ember', 'fable', 'grove', 'harbor', 'indigo', 'juniper', 'kodak', 'lumen', 'meadow', 'north', 'onyx', 'prism', 'quartz', 'reel', 'sable', 'tundra', 'umber', 'vista', 'willow', 'zenith']
  const r = crypto.getRandomValues(new Uint32Array(3))
  return `${words[r[0] % words.length]}-${words[r[1] % words.length]}-${100 + (r[2] % 900)}`
}

export function copy(text: string) {
  return navigator.clipboard?.writeText(text).catch(() => {
    const ta = document.createElement('textarea')
    ta.value = text
    document.body.appendChild(ta)
    ta.select()
    document.execCommand('copy')
    ta.remove()
  })
}

export function cls(...xs: (string | number | false | null | undefined)[]) {
  return xs.filter(Boolean).join(' ')
}

/** Interviews: takes grouped by their interview name, else slate scene, else alone. */
export type Interview = { key: string; name: string; takes: Take[]; duration: number; hidden: boolean; speakers: string[] }
export function interviews(takes: Take[], transcripts: Record<string, Transcript>): Interview[] {
  const groups = new Map<string, Take[]>()
  for (const t of [...takes].sort((a, b) => a.sort - b.sort)) {
    // A lettered pickup (102A) belongs with its scene (102).
    const key = t.interview ? `i:${t.interview}` : t.scene ? `s:${t.scene.replace(/[A-Za-z]+$/, '')}` : `t:${t.id}`
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key)!.push(t)
  }
  return [...groups.entries()].map(([key, ts]) => {
    const counts = new Map<string, number>()
    for (const t of ts)
      for (const seg of transcripts[t.id]?.segments || [])
        if (seg.speaker && seg.speaker !== '?' && seg.speaker !== 'Off-camera') {
          const n = speakerName(t, seg.speaker)
          counts.set(n, (counts.get(n) || 0) + seg.words.length)
        }
    const speakers = [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([n]) => n)
    const named = ts[0].interview
    const name = named || (speakers.length ? speakers.slice(0, 3).join(' & ') : ts.length > 1 ? `Scene ${ts[0].scene.replace(/[A-Za-z]+$/, '')}` : ts[0].name)
    return { key, name, takes: ts, duration: ts.reduce((n, t) => n + t.duration, 0), hidden: ts.every((t) => t.hidden), speakers }
  })
}

export const plural = (n: number, w: string, many = `${w}s`) => `${n} ${n === 1 ? w : many}`
